<script setup>
  import { ref, watch, nextTick } from "vue";
  import draggable from "vuedraggable";
  import { storeToRefs } from "pinia";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
  import { useSlideTemplatesStore } from "@stores/slideTemplates";
  import { useChartTemplatesStore } from "@stores/chartTemplates/chartTemplates";
  import { ScrollArea } from "@/components/ui/scroll-area";
  import { Button } from "@/components/ui/button";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { Badge } from "@/components/ui/badge";
  import { Tooltip } from "@/components/ui/tooltip";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import { Plus, Copy, Trash2, GripVertical } from "lucide-vue-next";
  import ReportSlidePreview from "./ReportSlidePreview.vue";
  import StreamingIndicator from "./StreamingIndicator.vue";

  const props = defineProps({
    slides: {
      type: Array,
      required: true,
    },
    selectedId: {
      type: [Number, String],
      default: null,
    },
  });

  const emit = defineEmits(["select", "delete", "add", "reorder", "insert-below", "duplicate"]);

  // Get data directly from stores (same source as editor) for proper reactivity
  const reportsStore = useReportsV2Store();
  const slideTemplatesStore = useSlideTemplatesStore();
  const chartTemplatesStore = useChartTemplatesStore();
  const { chartTemplates } = storeToRefs(chartTemplatesStore);
  const { currentReport } = storeToRefs(reportsStore);

  // Local copy for draggable (exclude streaming ghost slides)
  const localSlides = ref([...props.slides]);

  watch(
    () => props.slides,
    (newSlides) => {
      localSlides.value = [...newSlides];
    },
    { deep: true, immediate: true },
  );

  // Scroll selected slide into view (e.g. when AI creates a new slide)
  watch(
    () => props.selectedId,
    async (id) => {
      if (!id) return;
      await nextTick();
      const el = document.getElementById(`nav-menu-item-${id}`);
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    },
  );

  const hoveredItemId = ref(null);

  const {
    isOpen: showDeleteDialog,
    requestDelete: openDeleteDialog,
    confirm: confirmDelete,
  } = useConfirmDelete((slide) => emit("delete", slide.id));

  function getTemplate(slide) {
    return slideTemplatesStore.findTemplateForSlide(slide);
  }

  function getPreviewImageUrl(slide) {
    const template = getTemplate(slide);
    const skinType = template?.skin_type || 'content';
    const b64 = currentReport.value?.report_theme_backgrounds?.[skinType]?.preview_base64;
    return b64 ? `data:image/png;base64,${b64}` : null;
  }

  function handleDragEnd() {
    const positions = localSlides.value.map((slide, index) => ({
      id: slide.id,
      position: index + 1,
    }));
    emit("reorder", positions);
  }
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Empty state -->
    <div
      v-if="slides.length === 0"
      class="flex-1 flex items-center justify-center">
      <EmptyState title="Create your first slide" />
    </div>

    <ScrollArea
      v-else
      class="flex-1 min-h-0">
      <div class="px-4 py-3">
        <draggable
          v-model="localSlides"
          item-key="id"
          handle=".card-drag-handle"
          ghost-class="opacity-50"
          class="space-y-3"
          @end="handleDragEnd">
          <template #item="{ element: slide, index }">
            <div
              class="nav-menu__item-wrapper"
              data-cy="slides-list-item">
              <!-- Streaming ghost slide -->
              <!-- dead since #1980: nothing sets slide._streaming anymore, kept as
                   a landmark in case ghost slides come back. -->
              <div
                v-if="slide._streaming"
                class="rounded-lg border border-dashed border-primary/40 bg-card overflow-hidden">
                <div class="relative aspect-video bg-muted">
                  <ReportSlidePreview
                    :key="`streaming-${slide._streamingIndex}`"
                    :structure="getTemplate(slide)?.structure"
                    :bindings="reportsStore.getEffectiveBindings(slide)"
                    :editable-fields="getTemplate(slide)?.editable_fields || []"
                    :preview-image-url="getPreviewImageUrl(slide)"
                    :show-overlays="true"
                    :show-borders="false"
                    :chart-templates="chartTemplates" />
                  <!-- Streaming indicator overlay -->
                  <div class="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent pointer-events-none" />
                  <div class="absolute bottom-1.5 left-2">
                    <StreamingIndicator size="sm" />
                  </div>
                </div>
                <div class="px-2 flex items-center gap-2 border-t">
                  <span class="text-xs text-muted-foreground">{{ index + 1 }}</span>
                </div>
              </div>

              <!-- Normal slide -->
              <div
                v-else
                :id="`nav-menu-item-${slide.id}`"
                :class="{
                  'ring-2 ring-primary': selectedId === slide.id,
                }"
                class="rounded-lg border bg-card overflow-hidden cursor-pointer hover:border-primary/50 transition-all group"
                @click="$emit('select', slide.id)">
                <!-- Thumbnail -->
                <div
                  class="relative aspect-video bg-muted"
                  @mouseenter="hoveredItemId = slide.id"
                  @mouseleave="hoveredItemId = null">
                  <!-- Preview image -->
                  <ReportSlidePreview
                    :key="`preview-${slide.id}-${slide.slide_template_id}`"
                    :structure="getTemplate(slide)?.structure"
                    :bindings="reportsStore.getEffectiveBindings(slide)"
                    :editable-fields="getTemplate(slide)?.editable_fields || []"
                    :preview-image-url="getPreviewImageUrl(slide)"
                    :show-overlays="true"
                    :show-borders="false"
                    :chart-templates="chartTemplates" />

                  <!-- Drag handle -->
                  <div
                    :class="hoveredItemId === slide.id ? 'opacity-100' : 'opacity-0'"
                    class="card-drag-handle absolute top-2 left-2 p-1 rounded bg-background/80 transition-opacity cursor-grab active:cursor-grabbing">
                    <GripVertical class="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>

                <!-- Title and actions -->
                <div class="px-2 flex items-center justify-between gap-2 border-t">
                  <span class="text-xs text-muted-foreground">{{ index + 1 }}</span>
                  <div
                    class="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    @click.stop>
                    <Tooltip content="Insert below">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        class="text-muted-foreground hover:text-foreground"
                        @click="$emit('insert-below', slide.id)">
                        <Plus class="h-4 w-4" />
                      </Button>
                    </Tooltip>
                    <Tooltip content="Duplicate">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        class="text-muted-foreground hover:text-foreground"
                        @click="$emit('duplicate', slide.id)">
                        <Copy class="h-4 w-4" />
                      </Button>
                    </Tooltip>
                    <Tooltip content="Delete">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        class="text-muted-foreground hover:text-destructive"
                        @click="openDeleteDialog(slide)">
                        <Trash2 class="h-4 w-4" />
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </draggable>
      </div>
    </ScrollArea>

    <!-- Footer -->
    <div
      id="nav-pane-footer"
      class="py-2 px-3 border-t bg-background shrink-0 flex items-center justify-between">
      <Button
        variant="outline"
        class="gap-2"
        @click="$emit('add')">
        <Plus class="w-4 h-4" />
        Add Slide
      </Button>
      <Badge
        variant="secondary"
        class="rounded-full">
        {{ slides.length }} slide{{ slides.length !== 1 ? "s" : "" }}
      </Badge>
    </div>

    <ConfirmDialog
      v-model:open="showDeleteDialog"
      title="Delete Slide"
      description="Are you sure you want to delete this slide? This action cannot be undone."
      confirm-label="Delete"
      @confirm="confirmDelete" />
  </div>
</template>
